import {
  ChevronLeft,
  Headphones,
  MoreHorizontal,
  Music2,
  Play,
} from "lucide-react";

export interface WorkbookPreviewBlock {
  type: "prompt" | "journaling" | "challenge";
  localizedSectionTitle: string | null;
  title: string;
  prompt: string | null;
  instructions: string | null;
  challenge?: {
    title: string | null;
    audioFileName: string | null;
    durationSeconds: number | null;
    thumbnailUrl: string | null;
  } | null;
}

interface WorkbookMobilePreviewProps {
  pageTitle: string;
  pageSubtitle: string | null;
  categoryName: string | null;
  estimatedMinutes?: number | null;
  blocks: WorkbookPreviewBlock[];
}

interface SectionGroup {
  localizedSectionTitle: string | null;
  blocks: WorkbookPreviewBlock[];
}

function groupBlocksBySection(blocks: WorkbookPreviewBlock[]): SectionGroup[] {
  const groups: SectionGroup[] = [];
  for (const block of blocks) {
    const last = groups[groups.length - 1];
    const section = block.localizedSectionTitle?.trim() || null;
    if (last && (last.localizedSectionTitle || null) === section) {
      last.blocks.push(block);
    } else {
      groups.push({ localizedSectionTitle: section, blocks: [block] });
    }
  }
  return groups;
}

function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function StatusBar() {
  return (
    <div className="absolute top-0 left-0 right-0 h-[44px] z-30 flex items-center justify-between px-6 text-white">
      <span className="text-[15px] font-semibold">9:41</span>
      <div className="flex gap-1.5 items-center">
        <svg width="17" height="11" viewBox="0 0 17 11" fill="none">
          <rect x="0" y="2" width="3" height="9" rx="0.5" fill="white" />
          <rect x="4" y="0" width="3" height="11" rx="0.5" fill="white" />
          <rect
            x="8"
            y="3"
            width="3"
            height="8"
            rx="0.5"
            fill="white"
            opacity="0.8"
          />
          <rect
            x="12"
            y="5"
            width="3"
            height="6"
            rx="0.5"
            fill="white"
            opacity="0.5"
          />
        </svg>
        <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
          <path
            d="M7.5 10c-.7 0-1.3-.6-1.3-1.3 0-.7.6-1.3 1.3-1.3.7 0 1.3.6 1.3 1.3 0 .7-.6 1.3-1.3 1.3z"
            fill="white"
          />
          <path
            d="M10.6 6.4c-.8-.8-2-1.3-3.1-1.3-1.1 0-2.3.5-3.1 1.3l-.7-.7c1-1 2.4-1.6 3.8-1.6 1.4 0 2.8.6 3.8 1.6l-.7.7z"
            fill="white"
          />
          <path
            d="M13.4 3.5c-1.6-1.6-3.8-2.5-5.9-2.5-2.1 0-4.3.9-5.9 2.5l-.7-.7C2.7.9 5.1 0 7.5 0c2.4 0 4.8.9 6.6 2.8l-.7.7z"
            fill="white"
          />
        </svg>
        <div className="ml-1 w-[24px] h-[11px] border border-white rounded-[2.5px] relative">
          <div className="absolute right-[-3px] top-1/2 -translate-y-1/2 w-[1.5px] h-[4px] bg-white rounded-r-[1px]" />
          <div className="absolute inset-[1.5px] bg-white rounded-[1px]" />
        </div>
      </div>
    </div>
  );
}

function PageBackground() {
  return (
    <>
      <div
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, #2A1F18 0%, #1A130E 100%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 55% at 50% 35%, rgba(190, 140, 90, 0.32) 0%, rgba(120, 80, 50, 0.12) 35%, transparent 70%)",
        }}
      />
    </>
  );
}

function PromptEntry({
  block,
  isLast,
}: {
  block: WorkbookPreviewBlock;
  isLast: boolean;
}) {
  const placeholder = block.prompt?.trim() || "Enter here...";
  return (
    <div className={isLast ? "" : "pb-7"}>
      <h3 className="text-[16px] font-semibold text-white leading-[1.3] mb-3">
        {block.title || "Untitled prompt"}
      </h3>
      <p className="text-[14px] text-white/45 leading-[1.5] mb-4">
        {placeholder}
      </p>
      <div className="space-y-[18px]">
        <div className="h-px bg-white/12" />
        <div className="h-px bg-white/12" />
        <div className="h-px bg-white/12" />
      </div>
    </div>
  );
}

function JournalEntry({ block }: { block: WorkbookPreviewBlock }) {
  const placeholder =
    block.prompt?.trim() ||
    block.instructions?.trim() ||
    "Write whatever you want...";
  return (
    <div className="pb-2">
      {block.title && (
        <h3 className="text-[16px] font-semibold text-white leading-[1.3] mb-3">
          {block.title}
        </h3>
      )}
      <div className="rounded-[18px] bg-black/25 border border-white/8 px-4 py-4 min-h-[280px]">
        <p className="text-[14px] text-white/45 leading-[1.55]">
          {placeholder}
        </p>
      </div>
    </div>
  );
}

function ChallengeEntry({
  block,
  isLast,
}: {
  block: WorkbookPreviewBlock;
  isLast: boolean;
}) {
  const ch = block.challenge ?? null;
  const audioTitle = block.title || ch?.title || "Audio challenge";
  const duration = formatDuration(ch?.durationSeconds ?? null);
  const thumb = ch?.thumbnailUrl || null;

  return (
    <div className={isLast ? "" : "pb-7"}>
      <div className="flex items-center gap-2 mb-3">
        <Headphones
          className="w-[14px] h-[14px] text-[#D9C7A1]"
          strokeWidth={2}
        />
        <span className="text-[10.5px] font-semibold tracking-[0.16em] uppercase text-[#D9C7A1]">
          Audio
        </span>
      </div>
      <h3 className="text-[16px] font-semibold text-white leading-[1.3] mb-3">
        {audioTitle}
      </h3>
      {block.instructions && (
        <p className="text-[13px] text-white/55 leading-[1.55] mb-4 whitespace-pre-wrap">
          {block.instructions}
        </p>
      )}
      <div className="flex items-center gap-3 px-3 py-3 rounded-[14px] bg-black/30 border border-white/10">
        <div className="w-[44px] h-[44px] rounded-[10px] overflow-hidden bg-white/8 flex items-center justify-center shrink-0">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <Music2
              className="w-[18px] h-[18px] text-[#D9C7A1]"
              strokeWidth={2}
            />
          )}
        </div>
        <button
          type="button"
          className="w-[34px] h-[34px] rounded-full bg-white text-[#1F1611] flex items-center justify-center shrink-0"
          tabIndex={-1}
        >
          <Play
            className="w-[12px] h-[12px] ml-[1px] fill-current"
            strokeWidth={0}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="h-[3px] rounded-full bg-white/15 overflow-hidden">
            <div className="h-full w-[24%] rounded-full bg-[#D9C7A1]" />
          </div>
        </div>
        <span className="text-[10.5px] font-semibold text-white/65 tabular-nums shrink-0">
          {duration || "—:—"}
        </span>
      </div>
      {!isLast && <div className="h-px bg-white/12 mt-7" />}
    </div>
  );
}

function renderBlock(
  block: WorkbookPreviewBlock,
  isLast: boolean,
  key: string | number,
) {
  if (block.type === "challenge") {
    return <ChallengeEntry key={key} block={block} isLast={isLast} />;
  }
  if (block.type === "journaling") {
    return <JournalEntry key={key} block={block} />;
  }
  return <PromptEntry key={key} block={block} isLast={isLast} />;
}

export function WorkbookMobilePreview({
  pageTitle,
  pageSubtitle,
  blocks,
}: WorkbookMobilePreviewProps) {
  const groups = groupBlocksBySection(blocks);

  return (
    <div className="relative w-[375px] h-[812px] rounded-[44px] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] border border-black/20">
      <PageBackground />
      <StatusBar />

      <div className="absolute inset-0 overflow-y-auto">
        <div className="relative px-5 pt-[58px] pb-[120px]">
          <div className="flex items-center justify-between h-[44px] mb-3">
            <button
              type="button"
              className="w-[28px] h-[28px] flex items-center justify-center text-white"
              tabIndex={-1}
            >
              <ChevronLeft className="w-[22px] h-[22px]" strokeWidth={2} />
            </button>
            <button
              type="button"
              className="w-[28px] h-[28px] flex items-center justify-center text-white"
              tabIndex={-1}
            >
              <MoreHorizontal className="w-[22px] h-[22px]" strokeWidth={2} />
            </button>
          </div>

          <h1 className="font-serif italic text-white text-[30px] leading-[1.15] text-center">
            {pageTitle || "Untitled workbook"}
          </h1>
          {pageSubtitle && (
            <p className="text-[13px] text-white/65 text-center mt-2">
              {pageSubtitle}
            </p>
          )}
          <div className="mb-7" />

          {groups.length === 0 && (
            <div className="rounded-[24px] bg-black/15 border border-white/8 px-6 py-12 text-center">
              <p className="text-[13px] text-white/55">
                Add blocks to see them appear here.
              </p>
            </div>
          )}

          <div className="space-y-5">
            {groups.map((group, gi) => (
              <div
                key={gi}
                className="rounded-[24px] bg-black/15 border border-white/8 px-5 pt-5 pb-6"
              >
                {group.localizedSectionTitle && (
                  <p className="text-[13px] text-white/55 mb-4">
                    {group.localizedSectionTitle}
                  </p>
                )}
                {group.blocks.map((block, bi) => {
                  const isLast = bi === group.blocks.length - 1;
                  return renderBlock(block, isLast, bi);
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 left-0 right-0 pb-7 pt-5 px-5 z-20 pointer-events-none">
          <div
            className="absolute inset-x-0 bottom-0 h-[100px] -z-10"
            style={{
              background:
                "linear-gradient(to top, rgba(20, 14, 10, 0.9) 30%, transparent 100%)",
            }}
          />
          <button
            type="button"
            className="w-full py-4 rounded-full bg-white text-[#1F1611] text-[15px] font-semibold pointer-events-auto"
            tabIndex={-1}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
